// Timeline widget — chronologically orders entities with a `datetime` prop and
// renders a vertical timeline. Pure CSS, no heavyweight dep.
//
// Accepts:
//   props.events:  Array<{ id, title, time, kind?, body?, color? }>
//   props.height:  CSS height (default auto)
//   onDrill fires with event id when an entry is clicked.

import type { WidgetComponent } from "@viz/presentation";
import { useMemo } from "react";

interface RawEvent {
  id?: string;
  title?: string;
  time?: string | number | Date;
  kind?: string;
  body?: string;
  color?: string;
}

interface NormalisedEvent {
  id: string;
  title: string;
  time: Date;
  kind?: string;
  body?: string;
  color?: string;
}

const TimelineWidget: WidgetComponent = ({ props, onDrill }) => {
  const raw = (props.events as RawEvent[] | undefined) ?? [];
  const events = useMemo(() => normalise(raw), [raw]);
  const height = (props.height as string | number | undefined);

  if (events.length === 0) {
    return <div className="grid h-full min-h-[120px] place-items-center text-sm text-slate-500">No events.</div>;
  }

  return (
    <div className="overflow-auto px-4 py-3" style={height ? { height } : undefined}>
      <ol className="relative border-s border-slate-200 ps-6">
        {events.map(ev => (
          <li key={ev.id} className="mb-5">
            <span
              className="absolute -start-[7px] mt-1.5 inline-block h-3 w-3 rounded-full ring-2 ring-white"
              style={{ background: ev.color ?? "#0ea5e9" }}
            />
            <button
              onClick={() => onDrill?.(ev.id)}
              className="block text-left text-sky-700 hover:underline"
            >
              <div className="text-sm font-semibold text-slate-900">{ev.title}</div>
            </button>
            <div className="text-xs text-slate-500">
              {fmt(ev.time)}{ev.kind ? ` · ${ev.kind}` : ""}
            </div>
            {ev.body && (
              <p className="mt-1 text-sm text-slate-700">{ev.body}</p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

function normalise(rows: RawEvent[]): NormalisedEvent[] {
  const out: NormalisedEvent[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    const t = toDate(r.time);
    if (!t) continue;
    out.push({
      id: r.id ?? `event-${i}`,
      title: r.title ?? r.id ?? `Event ${i + 1}`,
      time: t,
      kind: r.kind,
      body: r.body,
      color: r.color,
    });
  }
  out.sort((a, b) => a.time.getTime() - b.time.getTime());
  return out;
}

function toDate(v: unknown): Date | null {
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  if (typeof v === "number") {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  return null;
}

const dtf = new Intl.DateTimeFormat(undefined, {
  year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
});
function fmt(d: Date): string { return dtf.format(d); }

export default TimelineWidget;
